import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BRANDING_ASSET_MAX_BYTES,
  BRANDING_ASSET_SLOTS,
  BRANDING_COLOR_KEYS,
  parseColor,
  type BrandingAssetSlot,
  type BrandingColorKey,
  type PlatformTenantCreated,
  type PlatformTenantDeletePreview,
  type PlatformTenantDeleted,
  type PlatformTenantDetail,
  type PlatformTenantUpdateInput,
} from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { FormCheck, MissingNote, useFormCheck } from "@/components/forms";
import { Button, CopyButton, ErrorNote, Field, inputClass } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, dateTime } from "@/lib/format";
import { ASSET_LABEL, AssetField, ColorField, GROUPS, emptyColors, kb, keepAll, readBase64, type AssetDraft, type Colors } from "@/pages/Branding";

/*
 * /platform — IL SUPER ADMIN E I TENANT DELLA SUA INSTALLAZIONE.
 *
 * La rotta si monta solo se `user.isPlatformAdmin` (App.tsx), e il backend
 * rifiuta comunque chiunque altro: qui si evita di mostrare porte chiuse, la'
 * si tengono chiuse.
 *
 * Quello che questa pagina NON fa, di proposito: entrare in un tenant, mostrare
 * clienti, conti o numeri di un tenant (l'elenco porta solo conteggi), dare il
 * flag di super admin (`pnpm platform:admin`), verificare un dominio o segnare
 * un certificato (a mano, con SQL: qui si vede lo stato e cosa manca).
 */

export function Platform() {
  const q = useQuery({ queryKey: ["platform", "tenants"], queryFn: api.platform.tenants });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PlatformTenantCreated | null>(null);
  const [deleted, setDeleted] = useState<PlatformTenantDeleted | null>(null);

  return (
    <>
      <PageHeader
        title="Tenant"
        subtitle="I gestori di questa installazione: solo conteggi, mai i loro dati"
        actions={
          !creating && (
            <Button variant="primary" onClick={() => { setCreating(true); setCreated(null); }}>
              Nuovo tenant
            </Button>
          )
        }
      />
      <div className="space-y-6 px-6 py-5">
        {created && <CreatedNote created={created} onClose={() => setCreated(null)} />}
        {deleted && (
          <section role="status" className="card max-w-2xl space-y-2 p-4">
            <h2 className="text-sm font-medium text-ink">
              Tenant «{deleted.deleted.name}» <span className="machine-id text-xs">{deleted.deleted.slug}</span> eliminato definitivamente
            </h2>
            <p className="text-xs text-muted">
              Righe cancellate: {Object.entries(deleted.counts).map(([m, n]) => `${m} ${n}`).join(", ")}. La riga di audit è nel tuo tenant.
            </p>
            <Button size="sm" onClick={() => setDeleted(null)}>
              Chiudi
            </Button>
          </section>
        )}
        {creating && (
          <CreateForm
            onCancel={() => setCreating(false)}
            onCreated={(c) => {
              setCreating(false);
              setCreated(c);
            }}
          />
        )}

        {q.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : q.error ? (
          <ErrorNote>{errorMessage(q.error)}</ErrorNote>
        ) : !q.data?.length ? (
          <p className="card px-4 py-4 text-sm text-muted">Nessun tenant. Crea il primo con «Nuovo tenant».</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Tenant</th>
                  <th className="py-2 pr-4 font-medium">Stato</th>
                  <th className="py-2 pr-4 text-right font-medium">Clienti</th>
                  <th className="py-2 pr-4 text-right font-medium">Conti prop</th>
                  <th className="py-2 pr-4 text-right font-medium">Conti broker</th>
                  <th className="py-2 pr-4 text-right font-medium">Fasi in corso</th>
                  <th className="py-2 pr-4 text-right font-medium">In run</th>
                  <th className="py-2 pr-4 font-medium">Ultima attività</th>
                  <th className="py-2 font-medium">Dominio</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelected(t.id === selected ? null : t.id)}
                    className={`cursor-pointer border-b border-line/60 align-top hover:bg-raised/60 ${t.id === selected ? "bg-raised" : ""}`}
                  >
                    <td className="py-2.5 pr-4">
                      <span className="text-ink">{t.displayName ?? t.name}</span>
                      <span className="machine-id ml-2 text-xs">{t.slug}</span>
                      {t.isHome && <span className="ml-2 text-2xs text-faint">il tuo</span>}
                    </td>
                    <td className="py-2.5 pr-4">{t.isActive ? <span className="text-gain">Attivo</span> : <span className="text-loss">Disattivato</span>}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{t.clients}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{t.propAccounts}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{t.brokerAccounts}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{t.liveSessions}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums">{t.openPairs}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted" title="L'ultima riga di audit del tenant">
                      {t.lastActivityAt ? dateTime(t.lastActivityAt) : "nessuna"}
                    </td>
                    <td className="py-2.5 text-muted">{t.customDomain ? <DomainBadge t={t} /> : <span className="text-faint">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-faint">Ultima attività = l'ultima riga di audit del tenant (azioni delle persone e cambi di stato). Clicca una riga per modificarla.</p>
          </div>
        )}

        {selected && (
          <TenantEditor
            key={selected}
            id={selected}
            onClose={() => setSelected(null)}
            onDeleted={(r) => {
              setSelected(null);
              setDeleted(r);
            }}
          />
        )}
      </div>
    </>
  );
}

function DomainBadge({ t }: { t: { customDomain: string | null; domainVerifiedAt: string | null; tlsIssuedAt: string | null } }) {
  return (
    <span>
      <span className="machine-id text-xs">{t.customDomain}</span>{" "}
      {!t.domainVerifiedAt ? <span className="text-warn">da verificare</span> : !t.tlsIssuedAt ? <span className="text-warn">certificato non registrato</span> : <span className="text-gain">ok</span>}
    </span>
  );
}

function CreateForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: PlatformTenantCreated) => void }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [email, setEmail] = useState("");
  const [inherit, setInherit] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = useFormCheck([
    ["Slug", !!slug.trim()],
    ["Nome", !!name.trim()],
    ["Email del primo ADMIN", !!email.trim()],
  ]);

  const submit = async () => {
    if (!check.attempt()) return;
    setError(null);
    setBusy(true);
    try {
      onCreated(await api.platform.create({ slug: slug.trim(), name: name.trim(), reportingCurrency: currency.trim() || undefined, adminEmail: email.trim(), inheritBranding: inherit }));
    } catch (e) {
      setError(errorMessage(e));
      check.fromError(e, {
        slug: "Slug",
        SLUG_TAKEN: "Slug",
        name: "Nome",
        reportingCurrency: "Valuta di reporting",
        adminEmail: "Email del primo ADMIN",
        EMAIL_TAKEN: "Email del primo ADMIN",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormCheck check={check}>
    <section className="card max-w-2xl space-y-4 p-4">
      <h2 className="text-sm font-medium text-ink">Nuovo tenant</h2>
      <p className="-mt-2 text-xs text-faint">
        Tenant e primo ADMIN nascono insieme o non nasce niente. La password la genera il gestionale e si vede una volta sola; al primo accesso va cambiata.
        Il catalogo delle prop firm si copia dal tuo tenant.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Slug" hint="Minuscole, cifre e trattini. Non si cambia dopo.">
          {(id) => <input id={id} value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className={`${inputClass} font-mono`} maxLength={40} spellCheck={false} />}
        </Field>
        <Field label="Nome">
          {(id) => <input id={id} value={name} onChange={(e) => setName(e.target.value)} className={inputClass} maxLength={120} />}
        </Field>
        <Field label="Valuta di reporting" hint="Tre lettere.">
          {(id) => <input id={id} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={`${inputClass} font-mono`} maxLength={3} />}
        </Field>
        <Field label="Email del primo ADMIN" hint="L'email di un accesso è unica in tutto il gestionale.">
          {(id) => <input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />}
        </Field>
      </div>
      <label className="flex items-start gap-2 text-sm text-ink">
        <input type="checkbox" checked={inherit} onChange={(e) => setInherit(e.target.checked)} className="mt-1" />
        <span>
          Eredita il marchio del tenant di casa
          <span className="block text-xs text-faint">Nome visualizzato, colori e immagini si copiano nel tenant nuovo; poi sono suoi e si cambiano da soli. Senza la spunta parte col marchio predefinito.</span>
        </span>
      </label>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => void submit()} disabled={busy}>
          Crea
        </Button>
        <Button onClick={onCancel} disabled={busy}>
          Annulla
        </Button>
        <MissingNote check={check} />
      </div>
    </section>
    </FormCheck>
  );
}

/** La password del primo ADMIN: si vede qui, una volta, e quando si chiude non la sa più nessuno. */
function CreatedNote({ created, onClose }: { created: PlatformTenantCreated; onClose: () => void }) {
  const c = created.catalog;
  return (
    <section role="status" className="card max-w-2xl space-y-3 border-accent/40 p-4">
      <h2 className="text-sm font-medium text-ink">Tenant «{created.tenant.name}» creato</h2>
      <dl className="grid grid-cols-[8rem_1fr] gap-y-1 text-sm">
        <dt className="text-muted">ADMIN</dt>
        <dd className="text-ink">{created.admin.email}</dd>
        <dt className="text-muted">Password</dt>
        <dd className="flex items-center gap-1">
          <span className="machine-id select-all">{created.admin.password}</span>
          <CopyButton value={created.admin.password} label="password" />
        </dd>
        <dt className="text-muted">Catalogo</dt>
        <dd className="text-ink">
          {c.firms || c.presets ? `${c.firms} prop firm e ${c.presets} programmi copiati da «${c.from}»` : `Niente copiato da «${c.from}»`}
          {c.note && <span className="block text-xs text-faint">{c.note}</span>}
        </dd>
        <dt className="text-muted">Marchio</dt>
        <dd className="text-ink">{created.branding ? `Copiato da «${created.branding.from}» (${created.branding.assets} immagini)` : "Predefinito"}</dd>
      </dl>
      <p className="text-xs text-warn">Si vede una volta sola: nel database c'è solo l'hash. Copiala nel gestore di password prima di chiudere.</p>
      <Button onClick={onClose}>L'ho copiata, chiudi</Button>
    </section>
  );
}

function TenantEditor({ id, onClose, onDeleted }: { id: string; onClose: () => void; onDeleted: (r: PlatformTenantDeleted) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["platform", "tenant", id], queryFn: () => api.platform.tenant(id) });
  const d = q.data;

  const [displayName, setDisplayName] = useState("");
  const [colors, setColors] = useState<Colors>(emptyColors);
  const [assets, setAssets] = useState<Record<BrandingAssetSlot, AssetDraft>>(keepAll);
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = (t: PlatformTenantDetail) => {
    setDisplayName(t.branding.displayName ?? "");
    setColors({ ...emptyColors(), ...t.branding.colors });
    setAssets((cur) => {
      for (const a of Object.values(cur)) if (a.kind === "new") URL.revokeObjectURL(a.objectUrl);
      return keepAll();
    });
    setDomain(t.customDomain ?? "");
  };
  useEffect(() => {
    if (d) reset(d);
  }, [d]);

  const parsed = useMemo(
    () => Object.fromEntries(BRANDING_COLOR_KEYS.map((k) => [k, colors[k].trim() ? parseColor(colors[k]) : undefined])) as Record<BrandingColorKey, ReturnType<typeof parseColor> | undefined>,
    [colors],
  );
  const invalid = BRANDING_COLOR_KEYS.filter((k) => parsed[k] === null);

  if (q.isLoading) return <p className="text-sm text-muted">Caricamento…</p>;
  if (q.error || !d) return <ErrorNote>{errorMessage(q.error)}</ErrorNote>;

  const assetUrl = (slot: BrandingAssetSlot): string | null => {
    const a = assets[slot];
    if (a.kind === "new") return a.objectUrl;
    if (a.kind === "remove") return null;
    return d.branding.assets[slot]?.url ?? null;
  };

  const brandingDirty =
    displayName.trim() !== (d.branding.displayName ?? "") ||
    BRANDING_COLOR_KEYS.some((k) => colors[k].trim() !== (d.branding.colors[k] ?? "")) ||
    BRANDING_ASSET_SLOTS.some((s) => assets[s].kind !== "keep");
  const domainDirty = domain.trim().toLowerCase() !== (d.customDomain ?? "");

  const pick = async (slot: BrandingAssetSlot, file: File | undefined) => {
    setError(null);
    setNote(null);
    if (!file) return;
    if (/svg/i.test(file.type) || /\.svg$/i.test(file.name)) {
      setError(`${ASSET_LABEL[slot].title}: SVG non accettato. Usa PNG, JPG, WebP o ICO.`);
      return;
    }
    const max = BRANDING_ASSET_MAX_BYTES[slot];
    if (file.size > max) {
      setError(`${ASSET_LABEL[slot].title}: ${kb(file.size)}, il massimo è ${kb(max)}.`);
      return;
    }
    try {
      const base64 = await readBase64(file);
      setAssets((cur) => {
        const prev = cur[slot];
        if (prev.kind === "new") URL.revokeObjectURL(prev.objectUrl);
        return { ...cur, [slot]: { kind: "new", base64, objectUrl: URL.createObjectURL(file), name: file.name, size: file.size } };
      });
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const removeAsset = (slot: BrandingAssetSlot) =>
    setAssets((cur) => {
      const prev = cur[slot];
      if (prev.kind === "new") URL.revokeObjectURL(prev.objectUrl);
      return { ...cur, [slot]: prev.kind === "new" ? { kind: "keep" } : { kind: "remove" } };
    });

  const send = async (body: PlatformTenantUpdateInput, ok: string) => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const saved = await api.platform.update(id, body);
      qc.setQueryData(["platform", "tenant", id], saved);
      setNote(ok);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const copyBranding = async () => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const saved = await api.platform.copyBranding(id);
      qc.setQueryData(["platform", "tenant", id], saved);
      void qc.invalidateQueries({ queryKey: ["platform", "tenants"] });
      setNote("Marchio copiato dal tenant di casa.");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const body: PlatformTenantUpdateInput = {};
    if (brandingDirty) {
      body.displayName = displayName.trim() || null;
      body.colors = Object.fromEntries(BRANDING_COLOR_KEYS.map((k) => [k, colors[k].trim() || null]));
      body.assets = {};
      for (const slot of BRANDING_ASSET_SLOTS) {
        const a = assets[slot];
        if (a.kind === "new") body.assets[slot] = { base64: a.base64 };
        if (a.kind === "remove") body.assets[slot] = null;
      }
    }
    if (domainDirty) body.customDomain = domain.trim() || null;
    void send(body, "Salvato.");
  };

  const mancante = !d.customDomain
    ? null
    : !d.domainVerifiedAt
      ? "Manca la verifica: record A del dominio verso la VPS e vhost Nginx, poi `domainVerifiedAt` a mano con SQL (DEPLOY.md §5). Finché manca, il dominio non mostra il marchio sul login e i link Telegram usano l'indirizzo comune."
      : !d.tlsIssuedAt
        ? "Manca il certificato: per i domini dei tenant non c'è un'emissione automatica (serve DNS-01 o un'emissione su richiesta). Emesso il certificato, `tlsIssuedAt` si scrive a mano con SQL."
        : null;

  return (
    <section className="card space-y-6 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium text-ink">
            {d.name} <span className="machine-id ml-1 text-xs">{d.slug}</span>
          </h2>
          <p className="text-xs text-faint">{d.isHome ? "Il tuo tenant di casa." : "Le modifiche finiscono nell'audit di questo tenant, a tuo nome."}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Chiudi
        </Button>
      </div>

      <div className="grid max-w-3xl gap-6">
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-ink">Dominio</h3>
          <Field label="Dominio custom" hint="Vuoto = nessuno. Se cambia, verifica e certificato tornano da fare.">
            {(fid) => <input id={fid} value={domain} onChange={(e) => setDomain(e.target.value)} className={`${inputClass} font-mono`} spellCheck={false} placeholder="app.gestore.it" />}
          </Field>
          {d.customDomain && (
            <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
              <dt className="text-muted">Dominio verificato</dt>
              <dd>{d.domainVerifiedAt ? <span className="text-gain">il {date(d.domainVerifiedAt)}</span> : <span className="text-warn">no</span>}</dd>
              <dt className="text-muted">Certificato emesso</dt>
              <dd>{d.tlsIssuedAt ? <span className="text-gain">il {date(d.tlsIssuedAt)}</span> : <span className="text-warn">non registrato</span>}</dd>
            </dl>
          )}
          {mancante && <p className="text-xs text-warn">{mancante}</p>}
        </section>

        <section className="space-y-4">
          <h3 className="text-sm font-medium text-ink">Marchio</h3>
          <Field label="Nome visualizzato" hint={`Vuoto = «${d.branding.tenantName}».`}>
            {(fid) => <input id={fid} value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputClass} maxLength={60} placeholder={d.branding.tenantName} />}
          </Field>
          <p className="text-xs text-faint">PNG, JPG, WebP o ICO; niente SVG. Logo fino a 500 KB, favicon fino a 100 KB.</p>
          {(["logoDark", "logoLight", "favicon"] as const).map((slot) => (
            <AssetField key={slot} slot={slot} url={assetUrl(slot)} draft={assets[slot]} saved={d.branding.assets[slot] ?? null} onPick={(f) => void pick(slot, f)} onRemove={() => removeAsset(slot)} />
          ))}
          <p className="text-xs text-faint">Colori: esadecimale, rgb(), hsl() o oklch(). Vuoto = tema predefinito.</p>
          {GROUPS.map((g) => (
            <fieldset key={g.title} className="rounded-card border border-line bg-surface p-3">
              <legend className="px-1 text-sm text-ink">{g.title}</legend>
              <p className="mb-2 text-xs text-faint">{g.hint}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {g.fields.map((f) => (
                  <ColorField key={f.key} name={f.key} label={f.label} value={colors[f.key]} parsed={parsed[f.key]} onChange={(v) => setColors((c) => ({ ...c, [f.key]: v }))} />
                ))}
              </div>
            </fieldset>
          ))}
        </section>

        {!d.isHome && (
          <section className="space-y-2 rounded-card border border-line bg-surface p-3">
            <h3 className="text-sm font-medium text-ink">Copia il marchio</h3>
            <p className="text-xs text-faint">Sovrascrive nome visualizzato, colori e immagini di questo tenant con quelli del tuo tenant di casa. Poi i due marchi sono indipendenti.</p>
            <Button
              disabled={busy || brandingDirty}
              title={brandingDirty ? "Salva o annulla prima le modifiche al marchio" : undefined}
              onClick={() => {
                if (window.confirm(`Sovrascrivere il marchio di «${d.name}» con quello del tuo tenant di casa? Nome visualizzato, colori e immagini attuali si perdono.`)) void copyBranding();
              }}
            >
              Copia il marchio dal tenant di casa
            </Button>
          </section>
        )}

        {invalid.length > 0 && <ErrorNote>Colori non validi: correggili o svuotali prima di salvare.</ErrorNote>}
        {error && <ErrorNote>{error}</ErrorNote>}
        {note && <p className="text-sm text-muted" role="status">{note}</p>}
        <div className="flex gap-2">
          <Button variant="primary" onClick={save} disabled={busy || (!brandingDirty && !domainDirty) || invalid.length > 0}>
            Salva
          </Button>
          <Button onClick={() => reset(d)} disabled={busy || (!brandingDirty && !domainDirty)}>
            Annulla
          </Button>
        </div>

        {/* Solo gli interruttori che l'installazione permette (.env): se nessuno, la sezione non esiste. */}
        {(d.featuresAvailable.indices || d.featuresAvailable.brokerPairs) && (
          <section className="space-y-2 border-t border-line pt-4">
            <h3 className="text-sm font-medium text-ink">Funzioni su misura</h3>
            <p className="text-xs text-faint">Spegnere un interruttore non tocca niente di ciò che esiste già.</p>
            {d.featuresAvailable.indices && (
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={d.indicesEnabled} disabled={busy} onChange={(e) => void send({ indicesEnabled: e.target.checked }, e.target.checked ? "Indici acceso." : "Indici spento.")} />
                Indici
              </label>
            )}
            {d.featuresAvailable.brokerPairs && (
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={d.brokerPairsEnabled}
                  disabled={busy}
                  onChange={(e) => void send({ brokerPairsEnabled: e.target.checked }, e.target.checked ? "Broker contro broker acceso." : "Broker contro broker spento.")}
                />
                Broker contro broker
              </label>
            )}
          </section>
        )}

        <section className="space-y-2 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Stato</h3>
          {d.isActive ? (
            <>
              <p className="text-xs text-faint">
                Disattivato, nessun suo utente entra né rinnova l'accesso (chi è dentro resta al più 15 minuti). Si rifiuta se il tenant ha coppie o operazioni singole aperte o conti
                collegati: le guardie non sorvegliano un tenant disattivato.
              </p>
              <Button variant="danger" disabled={busy || d.isHome} title={d.isHome ? "Il tuo tenant di casa non si disattiva" : undefined} onClick={() => void send({ isActive: false }, "Tenant disattivato.")}>
                Disattiva
              </Button>
            </>
          ) : (
            <Button onClick={() => void send({ isActive: true }, "Tenant riattivato.")} disabled={busy}>
              Riattiva
            </Button>
          )}
        </section>

        {/* Solo su un tenant disattivato: su uno attivo la sezione non esiste. */}
        {!d.isActive && (
          <DeleteTenant
            t={d}
            onDeleted={(r) => {
              qc.removeQueries({ queryKey: ["platform", "tenant", id] });
              void qc.invalidateQueries({ queryKey: ["platform", "tenants"] });
              onDeleted(r);
            }}
          />
        )}
      </div>
    </section>
  );
}

/**
 * ELIMINA DEFINITIVAMENTE: un tenant disattivato e vuoto (utenti, catalogo,
 * audit, impostazioni, note). Il pulsante chiede l'anteprima al backend — i
 * conteggi e i rifiuti li dice lui (`lib/tenant-delete.ts`), qui non si
 * riscrivono — e la conferma chiede di scrivere lo slug. Il backend lo
 * ricontrolla comunque.
 */
function DeleteTenant({ t, onDeleted }: { t: PlatformTenantDetail; onDeleted: (r: PlatformTenantDeleted) => void }) {
  const [preview, setPreview] = useState<PlatformTenantDeletePreview | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setError(null);
    setBusy(true);
    try {
      setTyped("");
      setPreview(await api.platform.deletePreview(t.id));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setError(null);
    setBusy(true);
    try {
      onDeleted(await api.platform.remove(t.id, typed));
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const righe = preview?.tables.filter((x) => x.count > 0) ?? [];
  const bloccato = (preview?.blockers.length ?? 0) > 0;
  const coincide = typed === t.slug;

  return (
    <section className="space-y-3 rounded-card border border-loss/40 bg-loss/5 p-4">
      <h3 className="text-sm font-medium text-loss">Elimina definitivamente</h3>
      <p className="text-xs text-muted">
        Cancella il tenant con utenti, catalogo, audit, impostazioni e note, e non si torna indietro. Si rifiuta se il tenant ha avuto clienti, conti, Fasi, cicli, operazioni o movimenti,
        anche archiviati o eliminati. La riga di audit resta nel tuo tenant.
      </p>

      {!preview ? (
        <Button variant="danger" disabled={busy} onClick={() => void open()}>
          {busy ? "Verifico…" : "Elimina definitivamente…"}
        </Button>
      ) : (
        <div className="space-y-3">
          {righe.length ? (
            <table className="text-sm">
              <tbody>
                {righe.map((x) => (
                  <tr key={x.model}>
                    <td className="py-0.5 pr-6 text-muted">{x.label}</td>
                    <td className="py-0.5 pr-4 text-right tabular-nums text-ink">{x.count}</td>
                    <td className="py-0.5 text-xs text-loss">{x.blocks ? "blocca" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted">Nessuna riga oltre al tenant.</p>
          )}

          {bloccato ? (
            <ul className="space-y-1 text-sm text-loss" role="alert">
              {preview.blockers.map((b) => (
                <li key={b.code}>{b.error}</li>
              ))}
            </ul>
          ) : (
            <Field label={`Per confermare scrivi lo slug: ${t.slug}`}>
              {(fid) => <input id={fid} value={typed} onChange={(e) => setTyped(e.target.value)} className={`${inputClass} font-mono`} spellCheck={false} autoComplete="off" />}
            </Field>
          )}

          <div className="flex gap-2">
            {!bloccato && (
              <Button variant="danger" disabled={busy || !coincide} onClick={() => void confirm()}>
                {busy ? "Elimino…" : "Elimina il tenant"}
              </Button>
            )}
            <Button
              disabled={busy}
              onClick={() => {
                setPreview(null);
                setError(null);
              }}
            >
              Annulla
            </Button>
          </div>
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </section>
  );
}
